// Diagnósticos · Mis empresas (ADR-007 · Contrato F1 · mcp_django apps/engagements).
// Las empresas cliente son orgs del SO a las que mi consultora entra por un engagement auditado.
//  - «Empresas que atiendo» (rol consultora): empresa, modo, estado, vigencia, «Trabajar aquí», bitácora.
//  - «Agregar empresa» → POST /engagements/empresas (org + engagement; checklist del alta).
//  - «Promover cliente existente» → POST /engagements/promover-cliente: vista previa (dry_run) y
//    solo con el clic de confirmación se aplica.
//  - «Quién tiene acceso a mi organización» (rol cliente): la EMPRESA pausa/reactiva/revoca o
//    cambia el modo (PATCH; a la consultora el backend le responde 403, por eso solo se ofrece aquí).
// Todo es de MI org: estas llamadas nunca llevan X-Acting-Org.
import { useCallback, useEffect, useState } from 'react';
import {
  ArrowRightLeft, Building2, CalendarClock, Check, History, Pause, Play, Plus, RefreshCw, ShieldCheck, ShieldOff, Sparkles, UserRoundCheck,
} from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { setActingOrg, useActingOrg } from '@/lib/actingOrg';
import {
  CONTEO_LABEL, ESTADO_LABEL, MODO_HINT, MODO_LABEL, SCOPE_LABEL,
  actualizarEngagement, bitacora, crearEmpresa, engagementBlocker, engagementUsable, listEngagements,
  listMyDiagClients, notifyEngagementsChanged, promoverCliente,
  type BitacoraEvento, type EmpresaCreada, type Engagement, type EngagementEstado, type EngagementModo, type PromocionResultado,
} from '@/lib/engagements';
import { DIAG_SCOPE } from '../api';
import { Chip, ErrorBox, Field, fmtDate, inputCls } from './ui';

const BRAND_BTN =
  'orion-btn-brand inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground transition disabled:cursor-not-allowed disabled:opacity-50';
const SEC_BTN =
  'inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50';
const DANGER_BTN =
  'inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl border border-destructive/40 bg-card px-3.5 py-2 text-sm font-medium text-destructive transition hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50';

const fmtDay = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
};

function EstadoChip({ e }: { e: Engagement }) {
  if (e.estado === 'revocado') return <Chip tone="bad">Revocado</Chip>;
  if (e.estado === 'pausado') return <Chip tone="warn">Pausado</Chip>;
  if (!e.vigente) return <Chip tone="warn">Vencido</Chip>;
  return <Chip tone="ok">{ESTADO_LABEL.activo}</Chip>;
}

function Vigencia({ e }: { e: Engagement }) {
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <CalendarClock size={13} aria-hidden />
      {e.vigente_hasta
        ? `${Date.parse(e.vigente_hasta) < Date.now() ? 'Venció el' : 'Vigente hasta'} ${fmtDay(e.vigente_hasta)}`
        : 'Sin fecha de término'}
      {e.created_at && <span>· desde {fmtDay(e.created_at)}</span>}
    </p>
  );
}

function ModoPicker({ value, onChange, name }: { value: EngagementModo; onChange: (m: EngagementModo) => void; name: string }) {
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-xs font-medium text-foreground">Modo de servicio</legend>
      {(['consultoria', 'con_colaboradores'] as EngagementModo[]).map((m) => (
        <label
          key={m}
          className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 text-sm transition ${value === m ? 'border-primary/60 bg-primary/10' : 'border-border hover:bg-muted'}`}
        >
          <input type="radio" name={name} value={m} checked={value === m} onChange={() => onChange(m)} className="mt-1 accent-[hsl(var(--primary))]" />
          <span className="min-w-0">
            <span className="block font-medium text-foreground">{MODO_LABEL[m]}</span>
            <span className="block text-xs text-muted-foreground">{MODO_HINT[m]}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

const PASO_LABEL: Record<string, string> = {
  org: 'Organización', modulo: 'Módulo de diagnósticos', llave_django: 'Llave de servicio', correo: 'Correo',
  config: 'Configuración', verificacion: 'Verificación',
};

function Checklist({ empresa }: { empresa: EmpresaCreada }) {
  if (!empresa.checklist.length) return null;
  return (
    <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
      {empresa.checklist.map((p) => (
        <li key={p.paso} className="flex items-start gap-1.5">
          <span aria-hidden className={`mt-1 h-2 w-2 shrink-0 rounded-full ${p.ok === true ? 'bg-emerald-500' : p.ok === false ? 'bg-destructive' : 'bg-muted-foreground/40'}`} />
          <span className="min-w-0">
            <span className="font-medium text-foreground">{PASO_LABEL[p.paso] || p.paso.replace(/_/g, ' ')}</span>
            <span className="sr-only">: {p.ok === true ? 'listo' : p.ok === false ? 'con error' : 'pendiente'}</span>
            {p.detalle && <span className="text-muted-foreground"> · {p.detalle}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function NuevaEmpresaModal({ onClose, onDone }: { onClose: () => void; onDone: (r: EmpresaCreada) => void }) {
  const [f, setF] = useState({ nombre: '', contacto: '', email: '' });
  const [modo, setModo] = useState<EngagementModo>('consultoria');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const emailOk = !f.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const valid = !!f.nombre.trim() && emailOk;
  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      onDone(await crearEmpresa({ nombre: f.nombre.trim(), modo, contacto_nombre: f.contacto.trim(), contacto_email: f.email.trim() }));
    } catch (err) {
      setError((err as Error).message || 'No pudimos crear la empresa.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      inShell
      onClose={onClose}
      title="Agregar empresa"
      size="md"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className={SEC_BTN} onClick={onClose}>Cancelar</button>
          <button type="button" className={BRAND_BTN} onClick={save} disabled={!valid || busy}>
            {busy ? <RefreshCw size={15} className="animate-spin" aria-hidden /> : <Plus size={15} aria-hidden />} Crear empresa
          </button>
        </div>
      }
    >
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <p className="text-sm text-muted-foreground">
          Se crea la empresa como organización del sistema y tu consultora queda con acceso a sus diagnósticos (ver,
          crear, editar y borrar). La empresa puede pausar o revocar ese acceso cuando quiera.
        </p>
        <ErrorBox error={error} />
        <Field label="Nombre de la empresa">
          <input className={inputCls} value={f.nombre} onChange={set('nombre')} autoFocus autoComplete="organization" required />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Contacto (opcional)" hint="Quien recibe el aviso de alta, p. ej. RR. HH.">
            <input className={inputCls} value={f.contacto} onChange={set('contacto')} autoComplete="name" />
          </Field>
          <Field label="Correo del contacto (opcional)">
            <input className={inputCls} type="email" value={f.email} onChange={set('email')} autoComplete="email" aria-invalid={!emailOk} />
          </Field>
        </div>
        {!emailOk && <p className="text-xs text-destructive">Revisa el correo del contacto.</p>}
        <ModoPicker value={modo} onChange={setModo} name="modo-nueva" />
      </form>
    </Modal>
  );
}

function PromoverModal({ onClose, onDone }: { onClose: () => void; onDone: (r: PromocionResultado) => void }) {
  const [clients, setClients] = useState<{ id: string; name: string; sector: string }[] | null>(null);
  const [clientId, setClientId] = useState('');
  const [nombre, setNombre] = useState('');
  const [modo, setModo] = useState<EngagementModo>('consultoria');
  const [preview, setPreview] = useState<PromocionResultado | null>(null);
  const [busy, setBusy] = useState<'' | 'preview' | 'run'>('');
  const [error, setError] = useState('');

  useEffect(() => {
    listMyDiagClients().then(setClients).catch((e) => {
      setClients([]);
      setError((e as Error).message || 'No pudimos cargar tus clientes.');
    });
  }, []);

  // Cambiar algo invalida la vista previa: se confirma exactamente lo que se vio.
  useEffect(() => setPreview(null), [clientId, modo, nombre]);

  const run = async (dry: boolean) => {
    if (!clientId || busy) return;
    setBusy(dry ? 'preview' : 'run');
    setError('');
    try {
      const r = await promoverCliente({ client_id: clientId, modo, dry_run: dry, ...(nombre.trim() ? { nombre: nombre.trim() } : {}) });
      if (dry) setPreview(r);
      else onDone(r);
    } catch (e) {
      setError((e as Error).message || 'No pudimos promover al cliente.');
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal
      open
      inShell
      onClose={onClose}
      title="Promover cliente existente"
      size="lg"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className={SEC_BTN} onClick={onClose}>Cancelar</button>
          {!preview ? (
            <button type="button" className={BRAND_BTN} onClick={() => run(true)} disabled={!clientId || !!busy}>
              {busy === 'preview' ? <RefreshCw size={15} className="animate-spin" aria-hidden /> : <Sparkles size={15} aria-hidden />} Ver vista previa
            </button>
          ) : (
            <button type="button" className={BRAND_BTN} onClick={() => run(false)} disabled={!!busy}>
              {busy === 'run' ? <RefreshCw size={15} className="animate-spin" aria-hidden /> : <ArrowRightLeft size={15} aria-hidden />} Promover y mover proyectos
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Convierte un cliente que hoy vive dentro de tu organización en una empresa con su propia cuenta en el sistema.
          Sus proyectos (con participantes, análisis y reportes) se mueven a la empresa y tu consultora conserva el acceso.
          Primero ves qué va a pasar; nada cambia hasta que confirmes.
        </p>
        <ErrorBox error={error} />
        <Field label="Cliente">
          <select className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!clients}>
            <option value="">{clients ? (clients.length ? 'Elige un cliente…' : 'No tienes clientes para promover') : 'Cargando…'}</option>
            {(clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.sector ? ` · ${c.sector}` : ''}</option>
            ))}
          </select>
        </Field>
        <Field label="Nombre de la empresa (opcional)" hint="Si lo dejas vacío se usa el nombre del cliente.">
          <input className={inputCls} value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="organization" />
        </Field>
        <ModoPicker value={modo} onChange={setModo} name="modo-promover" />
        {preview && (
          <section aria-label="Vista previa" className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Vista previa · aún no se aplica</p>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-muted-foreground">Cliente de hoy</dt>
                <dd className="font-medium text-foreground">{preview.cliente_nombre || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Empresa que se crea</dt>
                <dd className="font-medium text-foreground">
                  {preview.empresa_nombre || '—'}
                  {preview.empresa_org && <span className="block break-all text-xs font-normal text-muted-foreground">{preview.empresa_org}</span>}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-xs font-medium text-muted-foreground">Se mueve a la empresa</p>
            <ul className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Object.entries(preview.conteos).map(([k, n]) => (
                <li key={k} className="rounded-xl border border-border bg-card px-3 py-2">
                  <span className="block text-lg font-semibold tabular-nums text-foreground">{n.toLocaleString('es-MX')}</span>
                  <span className="block text-xs text-muted-foreground">{CONTEO_LABEL[k] || k.replace(/_/g, ' ')}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}

function BitacoraModal({ e, onClose }: { e: Engagement; onClose: () => void }) {
  const [eventos, setEventos] = useState<BitacoraEvento[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    bitacora(e.id).then(setEventos).catch((err) => {
      setEventos([]);
      setError((err as Error).message);
    });
  }, [e.id]);
  const ACCION: Record<string, string> = {
    creado: 'Acceso creado', acceso: 'Entró', acceso_sensible: 'Vio datos sensibles', denegado: 'Acceso negado',
    actualizado: 'Acceso cambiado', promovido: 'Cliente promovido',
  };
  return (
    <Modal open inShell onClose={onClose} title={`Bitácora · ${e.rol === 'consultora' ? e.cliente_nombre : e.consultora_nombre}`} size="lg">
      <ErrorBox error={error} />
      {eventos === null ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : eventos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sin movimientos registrados todavía.</p>
      ) : (
        <ol className="space-y-2">
          {eventos.map((ev) => (
            <li key={ev.id} className="rounded-xl border border-border px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{ACCION[ev.accion] || ev.accion}</span>
                {ev.scope && <Chip>{SCOPE_LABEL[ev.scope] || ev.scope}</Chip>}
                <span className="ml-auto text-xs text-muted-foreground">{fmtDate(ev.at)}</span>
              </div>
              <p className="mt-0.5 break-all text-xs text-muted-foreground">
                {ev.actor}{ev.endpoint ? ` · ${ev.endpoint}` : ''}{typeof ev.detalle.motivo === 'string' ? ` · ${ev.detalle.motivo}` : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

/** Acceso que MI org (empresa) otorgó a una consultora: la empresa lo controla. */
function AccesoOtorgado({ e, onChanged }: { e: Engagement; onChanged: () => void }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [log, setLog] = useState(false);
  const patch = async (k: string, body: Parameters<typeof actualizarEngagement>[1]) => {
    setBusy(k);
    setError('');
    try {
      await actualizarEngagement(e.id, body);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy('');
      setConfirmRevoke(false);
    }
  };
  const estado = (s: EngagementEstado) => patch(s, { estado: s });
  return (
    <li className="orion-card flex min-w-0 flex-col gap-3 rounded-[20px] border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck size={18} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-foreground">{e.consultora_nombre}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip tone="primary">{MODO_LABEL[e.modo]}</Chip>
            <EstadoChip e={e} />
            {e.scopes.map((s) => <Chip key={s}>{SCOPE_LABEL[s] || s}</Chip>)}
          </div>
        </div>
      </div>
      <Vigencia e={e} />
      <ErrorBox error={error} />
      <div className="flex flex-wrap items-center gap-2">
        {e.estado === 'activo' && (
          <button type="button" className={SEC_BTN} disabled={!!busy} onClick={() => estado('pausado')}>
            <Pause size={14} aria-hidden /> Pausar acceso
          </button>
        )}
        {e.estado !== 'activo' && (
          <button type="button" className={SEC_BTN} disabled={!!busy} onClick={() => estado('activo')}>
            <Play size={14} aria-hidden /> Reactivar
          </button>
        )}
        {e.estado !== 'revocado' && (confirmRevoke ? (
          <button type="button" className={DANGER_BTN} disabled={!!busy} onClick={() => estado('revocado')}>
            <ShieldOff size={14} aria-hidden /> Confirmar: revocar
          </button>
        ) : (
          <button type="button" className={DANGER_BTN} disabled={!!busy} onClick={() => setConfirmRevoke(true)}>
            <ShieldOff size={14} aria-hidden /> Revocar
          </button>
        ))}
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Modo
          <select
            className={`${inputCls} w-auto py-1`}
            value={e.modo}
            disabled={!!busy}
            onChange={(ev) => patch('modo', { modo: ev.target.value as EngagementModo })}
          >
            <option value="consultoria">{MODO_LABEL.consultoria}</option>
            <option value="con_colaboradores">{MODO_LABEL.con_colaboradores}</option>
          </select>
        </label>
        <button type="button" className={SEC_BTN} onClick={() => setLog(true)}>
          <History size={14} aria-hidden /> Bitácora
        </button>
      </div>
      {log && <BitacoraModal e={e} onClose={() => setLog(false)} />}
    </li>
  );
}

export function EmpresasView() {
  const { acting } = useActingOrg();
  const [list, setList] = useState<Engagement[] | null>(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<'' | 'nueva' | 'promover'>('');
  const [done, setDone] = useState<{ text: string; e: Engagement | null; empresa: EmpresaCreada | null } | null>(null);
  const [actError, setActError] = useState('');
  const [log, setLog] = useState<Engagement | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setList(await listEngagements());
    } catch (e) {
      setList([]);
      const status = (e as { status?: number }).status;
      setError(status === 404 ? 'Tu servidor todavía no tiene activado el acceso a empresas.' : (e as Error).message || 'No pudimos cargar tus empresas.');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const after = (text: string, e: Engagement | null, empresa: EmpresaCreada | null) => {
    setModal('');
    setDone({ text, e, empresa });
    notifyEngagementsChanged();
    void load();
  };

  const trabajar = (e: Engagement) => {
    setActError('');
    try {
      setActingOrg(e, DIAG_SCOPE);
    } catch (err) {
      setActError((err as Error).message);
    }
  };

  const atiendo = (list ?? []).filter((e) => e.rol === 'consultora');
  const meAtienden = (list ?? []).filter((e) => e.rol === 'cliente');

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Mis empresas</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Empresas que atiende tu consultora. Elige una para trabajar en sus diagnósticos; cada acceso queda en su bitácora.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" className={SEC_BTN} onClick={() => setModal('promover')}>
            <ArrowRightLeft size={15} aria-hidden /> Promover cliente existente
          </button>
          <button type="button" className={BRAND_BTN} onClick={() => setModal('nueva')}>
            <Plus size={15} aria-hidden /> Agregar empresa
          </button>
        </div>
      </div>

      {done && (
        <div role="status" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-foreground">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <Check size={16} aria-hidden className="shrink-0 text-emerald-600 dark:text-emerald-400" /> {done.text}
            </span>
            {done.e && engagementUsable(done.e, DIAG_SCOPE) && acting?.engagement_id !== done.e.id && (
              <button type="button" className={SEC_BTN} onClick={() => done.e && trabajar(done.e)}>
                Trabajar en {done.e.cliente_nombre}
              </button>
            )}
          </div>
          {done.empresa && done.empresa.estado && done.empresa.estado !== 'activo' && (
            <p className="mt-2 text-xs text-muted-foreground">
              El alta quedó {done.empresa.estado === 'con_error' ? 'con pasos pendientes (se pueden reintentar)' : 'en proceso'}:
            </p>
          )}
          {done.empresa && done.empresa.estado !== 'activo' && <Checklist empresa={done.empresa} />}
        </div>
      )}
      <ErrorBox error={error || actError} />

      <section aria-labelledby="emp-atiendo" className="space-y-3">
        <h3 id="emp-atiendo" className="text-sm font-semibold text-foreground">Empresas que atiendo</h3>
        {list === null ? (
          <ul className="grid gap-3 md:grid-cols-2" aria-busy="true" aria-label="Cargando empresas">
            {[0, 1].map((i) => <li key={i} className="h-[132px] animate-pulse rounded-[20px] border border-border bg-muted/40" />)}
          </ul>
        ) : atiendo.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-[20px] border border-dashed border-border px-4 py-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary"><Building2 size={20} aria-hidden /></span>
            <p className="text-sm font-medium text-foreground">Aún no atiendes ninguna empresa desde aquí</p>
            <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
              Agrega una empresa nueva o promueve a uno de tus clientes actuales para trabajar sus diagnósticos dentro del sistema.
            </p>
            <button type="button" className={`${BRAND_BTN} mt-2`} onClick={() => setModal('nueva')}>
              <Plus size={15} aria-hidden /> Agregar empresa
            </button>
          </div>
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {atiendo.map((e) => {
              const ok = engagementUsable(e, DIAG_SCOPE);
              const on = acting?.engagement_id === e.id;
              return (
                <li key={e.id} className={`orion-card flex min-w-0 flex-col gap-3 rounded-[20px] border bg-card p-4 ${on ? 'border-primary/60' : 'border-border'}`}>
                  <div className="flex items-start gap-3">
                    <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Building2 size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-foreground">{e.cliente_nombre}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Chip tone="primary">{MODO_LABEL[e.modo]}</Chip>
                        <EstadoChip e={e} />
                        {on && <Chip tone="ok"><UserRoundCheck size={11} aria-hidden /> Trabajando aquí</Chip>}
                      </div>
                    </div>
                  </div>
                  <Vigencia e={e} />
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    {ok ? (
                      <button type="button" className={on ? SEC_BTN : BRAND_BTN} onClick={() => trabajar(e)} disabled={on}>
                        {on ? 'Trabajando aquí' : 'Trabajar aquí'}
                        {!on && <span className="sr-only"> · {e.cliente_nombre}</span>}
                      </button>
                    ) : (
                      <p className="text-xs text-muted-foreground">No disponible: {engagementBlocker(e, DIAG_SCOPE)}.</p>
                    )}
                    <button type="button" className={SEC_BTN} onClick={() => setLog(e)}>
                      <History size={14} aria-hidden /> Bitácora
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {meAtienden.length > 0 && (
        <section aria-labelledby="emp-acceso" className="space-y-3">
          <div>
            <h3 id="emp-acceso" className="text-sm font-semibold text-foreground">Quién tiene acceso a mi organización</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">Consultoras que trabajan tus diagnósticos. Tú decides si siguen, se pausan o se revocan.</p>
          </div>
          <ul className="grid gap-3 md:grid-cols-2">
            {meAtienden.map((e) => <AccesoOtorgado key={e.id} e={e} onChanged={() => { notifyEngagementsChanged(); void load(); }} />)}
          </ul>
        </section>
      )}

      {modal === 'nueva' && (
        <NuevaEmpresaModal
          onClose={() => setModal('')}
          onDone={(r) => after(`Se creó la empresa «${r.nombre || r.engagement?.cliente_nombre}». Tu consultora ya tiene acceso.`, r.engagement, r)}
        />
      )}
      {modal === 'promover' && (
        <PromoverModal
          onClose={() => setModal('')}
          onDone={(r) => after(
            `«${r.empresa_nombre || 'El cliente'}» ya es una empresa del sistema${r.conteos.proyectos !== undefined ? ` · ${r.conteos.proyectos} proyecto(s) movidos` : ''}.`,
            r.empresa?.engagement ?? null,
            r.empresa,
          )}
        />
      )}
      {log && <BitacoraModal e={log} onClose={() => setLog(null)} />}
    </div>
  );
}
